import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { HttpStatusCode } from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { t } from 'i18next';
import { useDispatch } from 'react-redux';
import { useQueryClient } from '@tanstack/react-query';

import { user } from '@/context/permissionSlice';
import { settings } from '@/context/settingsSlice';
import { authEmployeeApi, authLogin } from '@/utils/Api/Authantication';
import { requestStorePasswordResetOtpV3Api, storeAuthLoginV3Api } from '@/utils/Api/AuthV3';
import type { StoreAuthV1OutletLogin } from '@/utils/Api/AuthV3/authV3.types';
import { clearCacheBeforeLogin } from '@/utils/queryCacheUtils';
import { useData } from '@/context/DataContext';
import type { LoginV3Location, LoginV3State, LoginV3Step } from './loginV3.types';
import { getProfileLoginEmailFromAuthPayload } from './loginV3ProfileLoginEmail';
import { loginV3DigitsOnly, loginV3IsEightDigitPhoneIdentifier, loginV3LooksLikeEmail } from './loginV3UserIdentifier';

const EMPTY_PASSCODE = ['', '', '', '', '', ''] as const;

function createInitialState(overrides?: Partial<LoginV3State>): LoginV3State {
    const base: LoginV3State = {
        step: 'login',
        login: { user: '', password: '' },
        department: { locations: [], selectedIndex: null },
        employee: { list: [], selectedIndex: null },
        accessCode: { digits: [...EMPTY_PASSCODE] },
        session: { loginDataRows: [], outletTokenIndex: 0, imageUrl: '' },
        reset: { email: '', otp: '' },
        isLoading: false,
        emailLoginRequiredAlert: false,
    };
    if (!overrides) return base;
    const { login: loginPatch, ...rest } = overrides;
    return {
        ...base,
        ...rest,
        login: loginPatch ? { ...base.login, ...loginPatch } : base.login,
    };
}

function resolveStepForState(prev: LoginV3State, step: LoginV3Step): LoginV3Step {
    if (step === 'department' && prev.department.locations.length <= 1) return 'login';
    if (step === 'employee' && prev.employee.list.length === 0) return 'login';
    if (step === 'accessCode' && prev.employee.list.length === 0) return 'login';
    if (step.startsWith('reset') && step !== 'resetEmail' && !prev.reset.email) return 'resetEmail';
    if ((step === 'resetOtp' || step === 'resetConfirm' || step === 'resetSuccess') && !prev.reset.email) {
        return 'resetEmail';
    }
    if ((step === 'resetConfirm' || step === 'resetSuccess') && !prev.reset.otp) return 'resetOtp';
    return step;
}

function stepChain(step: LoginV3Step, s: LoginV3State): { prev: LoginV3Step | null; next: LoginV3Step | null } {
    const resetChain: LoginV3Step[] = ['resetEmail', 'resetOtp', 'resetConfirm', 'resetSuccess'];
    if (resetChain.includes(step)) {
        const i = resetChain.indexOf(step);
        return {
            prev: i > 0 ? resetChain[i - 1]! : 'login',
            next: i < resetChain.length - 1 ? resetChain[i + 1]! : null,
        };
    }
    const cred: LoginV3Step[] = ['login', 'department', 'employee', 'accessCode'];
    const hasDept = s.department.locations.length > 1;
    const chain = hasDept ? cred : (['login', 'employee', 'accessCode'] as LoginV3Step[]);
    const idx = chain.indexOf(step);
    if (idx === -1) return { prev: null, next: null };
    return {
        prev: idx > 0 ? chain[idx - 1]! : null,
        next: idx < chain.length - 1 ? chain[idx + 1]! : null,
    };
}

export interface LoginV3FlowContextValue {
    state: LoginV3State;
    currentStep: LoginV3Step;
    previousStep: LoginV3Step | null;
    nextStep: LoginV3Step | null;
    setLoginCredentials: (patch: Partial<LoginV3State['login']>) => void;
    submitCredentialLogin: (
        e?: React.FormEvent<HTMLFormElement> | null,
        credentials?: { user: string; password: string },
    ) => Promise<void>;
    openPasswordReset: () => void;
    selectDepartment: (index: number) => void;
    selectEmployee: (index: number) => void;
    goNextInFlow: () => Promise<void>;
    goBackInFlow: () => void;
    onPasscodeValueChange: (value: string) => void;
    onPasscodeChange: (e: React.ChangeEvent<HTMLInputElement>, index: number) => void;
    onPasscodeKeyDown: (e: React.KeyboardEvent<HTMLInputElement>, index: number) => void;
    requestPasswordResetOtp: (args: { setter: (v: boolean) => void; email: string }) => Promise<boolean>;
    onResetEmailSuccess: (email: string) => void;
    onResetOtpVerified: (otp: string) => void;
    onResetPasswordSuccess: () => void;
    afterResetCompleteContinue: () => Promise<void>;
    dismissEmailLoginRequiredAlert: () => void;
}

const LoginV3FlowContext = createContext<LoginV3FlowContextValue | null>(null);

export function LoginV3FlowProvider({ children }: { children: React.ReactNode }) {
    const [state, setState] = useState<LoginV3State>(() => createInitialState());
    const navigate = useNavigate();
    const dispatchRedux = useDispatch();
    const queryClient = useQueryClient();
    const { refreshSettings } = useData();

    const stateRef = useRef(state);
    stateRef.current = state;

    const setLoginCredentials = useCallback((patch: Partial<LoginV3State['login']>) => {
        setState((s) => ({ ...s, login: { ...s.login, ...patch } }));
    }, []);

    const submitCredentialLogin = useCallback(
        async (e?: React.FormEvent<HTMLFormElement> | null, credentials?: { user: string; password: string }) => {
            let userTrim = '';
            let passwordVal = '';

            if (credentials) {
                e?.preventDefault?.();
                userTrim = credentials.user.trim();
                passwordVal = credentials.password;
            } else if (e?.currentTarget) {
                e.preventDefault();
                const fd = new FormData(e.currentTarget);
                userTrim = String(fd.get('user') ?? '').trim();
                passwordVal = String(fd.get('password') ?? '');
            } else {
                const snap = stateRef.current.login;
                userTrim = snap.user.trim();
                passwordVal = snap.password;
            }

            setState((s) => ({
                ...s,
                isLoading: true,
                login: { user: userTrim, password: passwordVal },
                emailLoginRequiredAlert: false,
            }));

            try {
                let payload: unknown;
                let rawAuthResponse: unknown;

                if (loginV3IsEightDigitPhoneIdentifier(userTrim)) {
                    rawAuthResponse = await authLogin({
                        payload: {
                            country_code: '+45',
                            phone_number: loginV3DigitsOnly(userTrim),
                            password: passwordVal,
                        },
                    });
                    const rawBody = rawAuthResponse;
                    if (Array.isArray(rawBody)) {
                        payload = rawBody;
                    } else if (rawBody && typeof rawBody === 'object' && 'data' in rawBody) {
                        const d = (rawBody as { data: unknown }).data;
                        if (Array.isArray(d)) {
                            payload = d;
                        } else {
                            payload = d;
                        }
                    } else {
                        payload = rawBody;
                    }
                } else {
                    const res = await storeAuthLoginV3Api({
                        email: userTrim,
                        password: passwordVal,
                    });
                    rawAuthResponse = res;
                    payload = res?.data;
                }

                if (loginV3IsEightDigitPhoneIdentifier(userTrim)) {
                    const enforcedLoginEmail =
                        getProfileLoginEmailFromAuthPayload(payload) ??
                        getProfileLoginEmailFromAuthPayload(rawAuthResponse);
                    if (enforcedLoginEmail) {
                        setState((s) => ({
                            ...s,
                            isLoading: false,
                            emailLoginRequiredAlert: true,
                        }));
                        return;
                    }
                }

                if (Array.isArray(payload)) {
                    const outlets = payload as StoreAuthV1OutletLogin[];
                    const locationData: LoginV3Location[] = outlets.map((location) => ({
                        name: location.profile?.name,
                        email: location.profile?.email,
                        image: location.profile?.profile_image
                            ? `${location.image_base_url ?? ''}${location.profile.profile_image}`
                            : '/default-profile.jpg',
                        image_base_url: location.image_base_url,
                        employees: (location.profile?.employees ?? []).map((employee) => ({
                            ...employee,
                            image: employee.image ? `${process.env.REACT_APP_IMG_URL}${employee.image}` : null,
                        })),
                        schedules: (location.profile?.schedules ?? []).map((schedule: unknown) => schedule),
                        is_active: location.is_active,
                    }));

                    setState((s) => {
                        if (locationData.length === 1) {
                            const loc = locationData[0]!;
                            if (!loc.is_active) {
                                toast.error(t('Common.StoreInactive'));
                                return { ...s, isLoading: false };
                            }
                            return {
                                ...s,
                                isLoading: false,
                                department: { locations: locationData, selectedIndex: 0 },
                                employee: { list: loc.employees, selectedIndex: null },
                                session: {
                                    loginDataRows: payload,
                                    outletTokenIndex: 0,
                                    imageUrl: (loc.image_base_url as string) || '',
                                },
                                step: 'employee',
                            };
                        }
                        return {
                            ...s,
                            isLoading: false,
                            department: { locations: locationData, selectedIndex: null },
                            session: { ...s.session, loginDataRows: payload, outletTokenIndex: 0, imageUrl: '' },
                            step: 'department',
                        };
                    });
                    return;
                }

                const accessToken =
                    payload &&
                    typeof payload === 'object' &&
                    !Array.isArray(payload) &&
                    'access_token' in payload &&
                    typeof (payload as { access_token?: unknown }).access_token === 'string'
                        ? (payload as { access_token: string }).access_token
                        : undefined;

                if (!accessToken) {
                    const p = payload as { detail?: string; msg?: string } | null;
                    toast.error(p?.detail ?? p?.msg ?? t('Common.LoginFailed'));
                    return;
                }

                localStorage.setItem('auth_token', accessToken);
                if (payload && typeof payload === 'object' && 'employee_role' in payload && payload.employee_role) {
                    localStorage.setItem('employee_role', String(payload.employee_role));
                }
                if (payload && typeof payload === 'object' && 'employee_id' in payload && payload.employee_id != null) {
                    localStorage.setItem('employee_id', String(payload.employee_id));
                }
                localStorage.setItem('image_url', process.env.REACT_APP_IMG_URL || '');

                await clearCacheBeforeLogin(queryClient);
                navigate('/calendar', { replace: true });

                setTimeout(() => {
                    refreshSettings();
                }, 500);
            } catch (error) {
                Sentry.logger.info('Error from handleLogin', {
                    message: error instanceof Error ? error.message : String(error),
                });
                const ax = error as {
                    response?: { data?: { detail?: string; msg?: string; message?: string } };
                    message?: string;
                };
                const body = ax.response?.data;
                const fromApi =
                    (typeof body?.detail === 'string' && body.detail) ||
                    (typeof body?.msg === 'string' && body.msg) ||
                    (typeof body?.message === 'string' && body.message);
                toast.error(fromApi || ax.message || t('Common.LoginFailed'));
            } finally {
                setState((s) => ({ ...s, isLoading: false }));
            }
        },
        [navigate, queryClient, refreshSettings],
    );

    const openPasswordReset = useCallback(() => {
        setState((s) => {
            const cleared = {
                ...s,
                department: { locations: [], selectedIndex: null },
                employee: { list: [], selectedIndex: null },
                accessCode: { digits: [...EMPTY_PASSCODE] },
                session: { loginDataRows: [], outletTokenIndex: 0, imageUrl: '' },
            };
            return {
                ...cleared,
                step: 'resetEmail' as LoginV3Step,
                emailLoginRequiredAlert: false,
                reset: {
                    email: (loginV3LooksLikeEmail(s.login.user) ? s.login.user.trim() : '') || s.reset.email,
                    otp: '',
                },
            };
        });
    }, []);

    const dismissEmailLoginRequiredAlert = useCallback(() => {
        setState((s) => ({ ...s, emailLoginRequiredAlert: false }));
    }, []);

    const selectDepartment = useCallback((index: number) => {
        setState((s) => {
            const loc = s.department.locations[index];
            if (!loc?.is_active) {
                toast.error(t('Common.StoreInactive'));
                return s;
            }
            return {
                ...s,
                department: { ...s.department, selectedIndex: index },
                employee: { list: loc.employees, selectedIndex: null },
                session: {
                    ...s.session,
                    outletTokenIndex: index,
                    imageUrl: (loc.image_base_url as string) || '',
                },
            };
        });
    }, []);

    const selectEmployee = useCallback((index: number) => {
        setState((s) => ({ ...s, employee: { ...s.employee, selectedIndex: index } }));
    }, []);

    const handleFinalLogin = useCallback(
        async ({
            employee,
            combinedPasscode,
            selectedLocationToken,
            employeesForStorage,
            imageUrlForStorage,
        }: {
            employee: Record<string, unknown>;
            combinedPasscode: string | undefined;
            selectedLocationToken: string;
            employeesForStorage: unknown[];
            imageUrlForStorage: string;
        }) => {
            try {
                const res = await authEmployeeApi({
                    token: selectedLocationToken,
                    employee_id: employee.id as number,
                    access_code: combinedPasscode,
                });

                if (
                    res &&
                    (res.status === HttpStatusCode.Ok || res.status === HttpStatusCode.Created) &&
                    res.data?.data?.access_token
                ) {
                    localStorage.setItem('auth_token', res.data.data.access_token);

                    const posSettings = res.data.data.settings?.find(
                        (setting: { settingCategory?: string; settingName?: string }) =>
                            setting.settingCategory === 'pos_settings' &&
                            setting.settingName === 'pos_general_settings',
                    );
                    const parsedPosSettings = posSettings?.value != null ? JSON.parse(posSettings.value) : null;
                    const empId = employee?.id;
                    const employeePOSPermissions =
                        empId != null && parsedPosSettings?.employeePermissions
                            ? (parsedPosSettings.employeePermissions as Record<string, unknown>)[String(empId)] || null
                            : null;
                    const userWithPermissions = { ...employee, pos_settings: employeePOSPermissions };

                    dispatchRedux(user(userWithPermissions));
                    dispatchRedux(settings({ from_dashboard: false }));

                    localStorage.setItem('employee_role', employee.role as string);
                    localStorage.setItem('employee_id', String(employee.id));
                    localStorage.setItem('employees', JSON.stringify(employeesForStorage));
                    localStorage.setItem('image_url', imageUrlForStorage);

                    await clearCacheBeforeLogin(queryClient);

                    navigate('/calendar', { replace: true });

                    setTimeout(() => {
                        refreshSettings();
                    }, 1500);
                }
            } catch (error) {
                console.log('error', error);
                toast.error(t('Calendar.ToastErrPasscode'));
            } finally {
                setState((s) => ({ ...s, isLoading: false }));
            }
        },
        [dispatchRedux, navigate, queryClient, refreshSettings],
    );

    const goNextInFlow = useCallback(async () => {
        const s = stateRef.current;
        const { step } = s;
        const employee = s.employee.selectedIndex !== null ? s.employee.list[s.employee.selectedIndex] : null;
        const combinedPasscode = s.accessCode.digits.join('');

        if (step === 'department' && s.department.selectedIndex !== null) {
            setState((prev) => ({ ...prev, step: resolveStepForState(prev, 'employee') }));
            return;
        }

        if (step === 'employee' && s.employee.selectedIndex !== null) {
            const rows = s.session.loginDataRows as Record<string, unknown>[];
            const idx = s.session.outletTokenIndex;
            const isBypass = rows[idx]?.by_pass_access_code as boolean | undefined;
            if (isBypass) {
                setState((prev) => ({ ...prev, isLoading: true }));
                await handleFinalLogin({
                    employee: employee as Record<string, unknown>,
                    combinedPasscode: undefined,
                    selectedLocationToken: rows[idx]?.access_token as string,
                    employeesForStorage: s.employee.list,
                    imageUrlForStorage: s.session.imageUrl,
                });
                return;
            }
            setState((prev) => ({ ...prev, step: resolveStepForState(prev, 'accessCode') }));
            return;
        }

        if (step === 'accessCode' && combinedPasscode.length === 6) {
            if (!employee) {
                toast.error(t('Calendar.ToastErrEmpSelect'));
                return;
            }

            const rows = s.session.loginDataRows as Record<string, unknown>[];
            const selectedLocationToken = rows[s.session.outletTokenIndex]?.access_token as string;

            setState((prev) => ({ ...prev, isLoading: true }));
            await handleFinalLogin({
                employee: employee as Record<string, unknown>,
                combinedPasscode,
                selectedLocationToken,
                employeesForStorage: s.employee.list,
                imageUrlForStorage: s.session.imageUrl,
            });
        }
    }, [handleFinalLogin]);

    const goBackInFlow = useCallback(() => {
        const s = stateRef.current;
        const { step } = s;
        if (step === 'resetEmail') {
            setState(createInitialState({ login: s.login }));
            return;
        }
        if (step === 'resetOtp') {
            setState((prev) => ({ ...prev, step: resolveStepForState(prev, 'resetEmail') }));
            return;
        }
        if (step === 'resetConfirm') {
            setState((prev) => ({ ...prev, step: resolveStepForState(prev, 'resetOtp') }));
            return;
        }
        if (step === 'resetSuccess') {
            setState((prev) => ({ ...prev, step: resolveStepForState(prev, 'resetConfirm') }));
            return;
        }
        if (step === 'department') {
            setState(createInitialState({ login: s.login }));
            return;
        }
        if (step === 'employee') {
            if (s.department.locations.length > 1) {
                setState((prev) => ({
                    ...prev,
                    employee: { ...prev.employee, selectedIndex: null },
                    step: resolveStepForState(prev, 'department'),
                }));
            } else {
                setState(createInitialState({ login: s.login }));
            }
            return;
        }
        if (step === 'accessCode') {
            setState((prev) => ({
                ...prev,
                step: resolveStepForState(prev, 'employee'),
                accessCode: { digits: [...EMPTY_PASSCODE] },
            }));
        }
    }, []);

    const onPasscodeChange = useCallback((e: React.ChangeEvent<HTMLInputElement>, index: number) => {
        const raw = e.target.value.replace(/\D/g, '');
        const val = raw.slice(-1);
        setState((s) => {
            const digits = [...s.accessCode.digits];
            digits[index] = val;
            return { ...s, accessCode: { digits } };
        });
        if (val && index < EMPTY_PASSCODE.length - 1) {
            requestAnimationFrame(() => {
                document.getElementById(`passcode-${index + 1}`)?.focus();
            });
        }
    }, []);

    const onPasscodeKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>, index: number) => {
        if (e.key !== 'Backspace') return;
        setState((s) => {
            const digits = [...s.accessCode.digits];
            if (digits[index]) {
                digits[index] = '';
                return { ...s, accessCode: { digits } };
            }
            if (index > 0) {
                digits[index - 1] = '';
                requestAnimationFrame(() => {
                    document.getElementById(`passcode-${index - 1}`)?.focus();
                });
                return { ...s, accessCode: { digits } };
            }
            return s;
        });
    }, []);

    const onPasscodeValueChange = useCallback((value: string) => {
        const numeric = value.replace(/\D/g, '').slice(0, EMPTY_PASSCODE.length);
        const digits = Array.from({ length: EMPTY_PASSCODE.length }, (_, idx) => numeric[idx] || '');
        setState((s) => ({ ...s, accessCode: { digits } }));
    }, []);

    const requestPasswordResetOtp = useCallback(
        async ({ setter, email: emailVal }: { setter: (v: boolean) => void; email: string }) => {
            try {
                setter(true);
                await requestStorePasswordResetOtpV3Api({ email: emailVal.trim() });
                toast.success(t('Common.OtpSentSuccess'));
                return true;
            } catch (error: unknown) {
                const err = error as { response?: { data?: { detail?: string; msg?: string } } };
                toast.error(
                    err?.response?.data?.detail || err?.response?.data?.msg || t('Common.LoginResetPasswordError'),
                );
                return false;
            }
        },
        [],
    );

    const onResetEmailSuccess = useCallback((emailVal: string) => {
        setState((prev) => ({
            ...prev,
            reset: { ...prev.reset, email: emailVal },
            step: resolveStepForState({ ...prev, reset: { ...prev.reset, email: emailVal } }, 'resetOtp'),
        }));
    }, []);

    const onResetOtpVerified = useCallback((otp: string) => {
        setState((prev) => ({
            ...prev,
            reset: { ...prev.reset, otp },
            step: resolveStepForState({ ...prev, reset: { ...prev.reset, otp } }, 'resetConfirm'),
        }));
    }, []);

    const onResetPasswordSuccess = useCallback(() => {
        setState((prev) => ({ ...prev, step: resolveStepForState(prev, 'resetSuccess') }));
    }, []);

    const afterResetCompleteContinue = useCallback(async () => {
        setState(createInitialState());
        await clearCacheBeforeLogin(queryClient);
    }, [queryClient]);

    const chain = useMemo(() => stepChain(state.step, state), [state]);

    const value = useMemo<LoginV3FlowContextValue>(
        () => ({
            state,
            currentStep: state.step,
            previousStep: chain.prev,
            nextStep: chain.next,
            setLoginCredentials,
            submitCredentialLogin,
            openPasswordReset,
            selectDepartment,
            selectEmployee,
            goNextInFlow,
            goBackInFlow,
            onPasscodeValueChange,
            onPasscodeChange,
            onPasscodeKeyDown,
            requestPasswordResetOtp,
            onResetEmailSuccess,
            onResetOtpVerified,
            onResetPasswordSuccess,
            afterResetCompleteContinue,
            dismissEmailLoginRequiredAlert,
        }),
        [
            state,
            chain.prev,
            chain.next,
            setLoginCredentials,
            submitCredentialLogin,
            openPasswordReset,
            dismissEmailLoginRequiredAlert,
            selectDepartment,
            selectEmployee,
            goNextInFlow,
            goBackInFlow,
            onPasscodeValueChange,
            onPasscodeChange,
            onPasscodeKeyDown,
            requestPasswordResetOtp,
            onResetEmailSuccess,
            onResetOtpVerified,
            onResetPasswordSuccess,
            afterResetCompleteContinue,
        ],
    );

    return <LoginV3FlowContext.Provider value={value}>{children}</LoginV3FlowContext.Provider>;
}

export function useLoginV3Flow(): LoginV3FlowContextValue {
    const ctx = useContext(LoginV3FlowContext);
    if (!ctx) {
        throw new Error('useLoginV3Flow must be used within LoginV3FlowProvider');
    }
    return ctx;
}
